'use client'

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { partyBridge } from './party-bridge'

/** DIAG temporaire (à retirer) : confirme côté serveur que la reprise après
 *  reload forcé (AutoUpdate) se déclenche bien, et avec quel résultat. */
function resumeBeacon(msg: string) {
  try { const x = new XMLHttpRequest(); x.open('POST', '/api/tvlog', true); x.send('[resume] ' + msg) } catch {}
}

export type PlayerTrack = {
  videoId: string          // YouTube videoId (ou ID synthétique pour podcast)
  title: string
  artist: string
  thumbnail?: string
  durationSec?: number
  // === Podcast / audio direct ===
  // Si défini, la lecture utilise CETTE URL audio au lieu de résoudre via
  // YouTube. Permet de jouer les épisodes de podcast (MP3 direct du flux RSS)
  // dans le lecteur principal, avec l'affichage PodcastDisplay.
  audioUrl?: string
  isPodcast?: boolean
  // === Vidéo (BLC Video Player) partagée sur la baffle ===
  // Si défini, le lecteur affiche l'IMAGE de la vidéo (slot vidéo) au lieu du
  // vinyle, et propose « Traduire FR » à la place du karaoké.
  isVideo?: boolean
}

export type RepeatMode = 'off' | 'one' | 'all'

type Ctx = {
  current: PlayerTrack | null
  queue: PlayerTrack[]
  playing: boolean
  duration: number          // seconds
  ready: boolean
  volume: number            // 0-100
  // UI modes
  videoFullscreen: boolean
  audioOnly: boolean
  videoPip: boolean              // vidéo en petite fenêtre flottante (PiP)
  setVideoFullscreen: (v: boolean) => void
  setAudioOnly: (v: boolean) => void
  setVideoPip: (v: boolean) => void
  // Playback modes
  repeat: RepeatMode        // 'off' | 'one' | 'all'
  shuffle: boolean
  cycleRepeat: () => void   // off → all → one → off
  toggleShuffle: () => void
  // controls
  play: (t: PlayerTrack, queue?: PlayerTrack[]) => void
  toggle: () => void
  /** Force l'état de lecture (true=play, false=pause), idempotent. */
  setPlay: (want: boolean) => void
  /** Arrête la lecture et ferme le lecteur (vide la file). */
  stop: () => void
  next: () => void
  prev: () => void
  seek: (sec: number) => void
  setVolume: (v: number) => void
  enqueue: (t: PlayerTrack | PlayerTrack[]) => void
  /** Insère un morceau JUSTE APRÈS le morceau courant (« Lire ensuite »). */
  playNext: (t: PlayerTrack) => void
  /** Retire un morceau de la file d'attente. */
  removeFromQueue: (videoId: string) => void
  /** Déplace le morceau de l'index `from` pour l'insérer AVANT l'index `to`
   *  (0..length). Le morceau en cours reste suivi (l'index s'ajuste). */
  reorderQueue: (from: number, to: number) => void
  // internal hooks for the iframe component
  _attachPlayer: (api: PlayerApi) => void
  _onState: (state: { playing?: boolean; duration?: number; position?: number; ready?: boolean }) => void
  _onEnded: () => void
}

type PlayerApi = {
  // audioUrl optionnel : si fourni (podcast), le player joue directement cette
  // URL au lieu de résoudre via YouTube.
  load: (videoId: string, autoplay: boolean, audioUrl?: string) => void
  playPause: (play: boolean) => void
  seekTo: (sec: number) => void
  setVolume?: (v: number) => void
}

const PlayerContext = createContext<Ctx | null>(null)
// ⚡ PERF (anti-crash) : la POSITION de lecture (mise à jour ~4×/s) vit dans un
// contexte SÉPARÉ. Sinon elle était dans la valeur de PlayerContext → la valeur
// changeait 4×/s → TOUS les consommateurs de usePlayer() (PlayerBar, mini-lecteur,
// ET les listes recherche/accueil) se re-rendaient 4×/s en continu → churn mémoire
// → OOM/crash du WebView mobile après quelques minutes. Ici, seuls les composants
// de progression s'abonnent à usePlayerPosition() ; le reste reste stable.
const PlayerPositionContext = createContext<number>(0)

export function PlayerProvider({ children }: { children: React.ReactNode }) {
  const [queue, setQueue] = useState<PlayerTrack[]>([])
  const [index, setIndex] = useState(-1)
  const [playing, setPlaying] = useState(false)
  const [duration, setDuration] = useState(0)
  const [position, setPosition] = useState(0)
  const [ready, setReady] = useState(false)
  const [videoFullscreen, setVideoFullscreen] = useState(false)
  const [audioOnly, setAudioOnly] = useState(false)
  const [videoPip, setVideoPip] = useState(false)
  // Repeat + Shuffle + Volume persistés — relus APRÈS le montage (effet plus
  // bas), jamais dans useState : le serveur rend toujours les valeurs par
  // défaut, et lire localStorage au 1er rendu client créait un écart
  // d'hydratation (React #418) → React reconstruisait la page et la TV
  // verticale perdait sa rotation (data-tvrot) → affichage horizontal.
  const [repeat, setRepeat] = useState<RepeatMode>('off')
  const [shuffle, setShuffle] = useState<boolean>(false)
  const cycleRepeat = useCallback(() => {
    setRepeat(r => {
      const next = r === 'off' ? 'all' : r === 'all' ? 'one' : 'off'
      try { localStorage.setItem('blc.repeat', next) } catch {}
      return next
    })
  }, [])
  const toggleShuffle = useCallback(() => {
    setShuffle(s => {
      const next = !s
      try { localStorage.setItem('blc.shuffle', next ? '1' : '0') } catch {}
      return next
    })
  }, [])
  const [volume, setVolumeState] = useState<number>(70)

  const volumeRef = useRef(volume)
  useEffect(() => { volumeRef.current = volume }, [volume])

  const apiRef = useRef<PlayerApi | null>(null)

  useEffect(() => {
    try {
      const r = localStorage.getItem('blc.repeat')
      if (r === 'all' || r === 'one') setRepeat(r)
      if (localStorage.getItem('blc.shuffle') === '1') setShuffle(true)
      const v = parseInt(localStorage.getItem('blc.volume') || '', 10)
      if (!isNaN(v)) {
        const clamped = Math.max(0, Math.min(100, v))
        volumeRef.current = clamped
        setVolumeState(clamped)
        apiRef.current?.setVolume?.(clamped)   // lecteur déjà attaché (effet enfant)
      }
    } catch {}
  }, [])

  const current = index >= 0 ? queue[index] ?? null : null

  // === REPRISE après rechargement forcé (AutoUpdate) ===
  // « La musique s'arrête au bout d'un moment » : AutoUpdate force un
  // location.reload() ~3 min après un déploiement (sinon la TV reste bloquée
  // sur l'ancien code) — mais RIEN ne relançait la lecture après → silence.
  // Ici : juste avant CE reload, on sauvegarde piste/file/position/lecture ;
  // dès que le lecteur natif s'attache, on restaure et on reprend EXACTEMENT
  // là où on en était (quelques secondes de coupure, pas un arrêt définitif).
  const stateRef = useRef({ current, queue, index, position, playing })
  useEffect(() => { stateRef.current = { current, queue, index, position, playing } })
  useEffect(() => {
    if (typeof window === 'undefined') return
    const save = () => {
      try {
        const s = stateRef.current
        if (!s.current) return
        // File tronquée autour de la piste en cours (borne la taille stockée) et
        // pochettes data: trop lourdes retirées (TrackThumb régénère via videoId).
        const WINDOW = 60
        const start = Math.max(0, s.index - 5)
        const end = Math.min(s.queue.length, start + WINDOW)
        const trimmed = s.queue.slice(start, end).map(t => ({
          ...t,
          thumbnail: t.thumbnail && t.thumbnail.startsWith('data:') && t.thumbnail.length > 300 ? undefined : t.thumbnail,
        }))
        localStorage.setItem('blc.resume', JSON.stringify({
          ts: Date.now(), current: s.current, queue: trimmed,
          index: Math.max(0, s.index - start), position: s.position, playing: s.playing,
        }))
      } catch {}
    }
    window.addEventListener('blc-before-reload', save)
    window.addEventListener('pagehide', save)
    return () => {
      window.removeEventListener('blc-before-reload', save)
      window.removeEventListener('pagehide', save)
    }
  }, [])
  const resumeAppliedRef = useRef(false)

  const play = useCallback((t: PlayerTrack, q?: PlayerTrack[]) => {
    // SOIRÉE (invité) — destination « baffle » : on n'écoute pas en local, on
    // envoie le morceau à la baffle. (L'hôte n'est jamais inParty ici : il joue
    // localement, ce qui EST la baffle.)
    if (partyBridge.inParty && partyBridge.target === 'baffle') {
      try { partyBridge.send(t) } catch {}
      return
    }
    // Un choix explicite prime sur la restauration différée au démarrage.
    resumeAppliedRef.current = true
    let nextQueue: PlayerTrack[]
    let nextIndex: number
    if (q && q.length) {
      nextQueue = q
      nextIndex = q.findIndex(x => x.videoId === t.videoId)
      if (nextIndex < 0) { nextQueue = [t, ...q]; nextIndex = 0 }
    } else {
      // ensure track is in the queue
      const exists = queue.findIndex(x => x.videoId === t.videoId)
      if (exists >= 0) { nextQueue = queue; nextIndex = exists }
      else { nextQueue = [...queue, t]; nextIndex = nextQueue.length - 1 }
    }
    setQueue(nextQueue)
    setIndex(nextIndex)
    setPlaying(true)
    setPosition(0)
    setDuration(t.durationSec || 0)
    stateRef.current = { current: t, queue: nextQueue, index: nextIndex, position: 0, playing: true }
    apiRef.current?.load(t.videoId, true, t.audioUrl)
  }, [queue])

  const toggle = useCallback(() => {
    if (!current) return
    const want = !playing
    setPlaying(want)
    apiRef.current?.playPause(want)
  }, [current, playing])

  // Force l'état de lecture désiré (idempotent) — fiable pour les commandes
  // distantes de la baffle (pas de dépendance à un état "playing" potentiellement
  // désynchronisé, donc ça marche au 1er clic).
  const setPlay = useCallback((want: boolean) => {
    if (!current) return
    setPlaying(want)
    apiRef.current?.playPause(want)
  }, [current])

  // FERMER le lecteur : stoppe la lecture et vide la file → la PlayerBar
  // disparaît (elle se masque quand `current` est null).
  const stop = useCallback(() => {
    resumeAppliedRef.current = true
    try { localStorage.removeItem('blc.resume') } catch {}
    try { apiRef.current?.playPause(false) } catch {}
    setPlaying(false); setPosition(0); setIndex(-1); setQueue([])
  }, [])

  const next = useCallback(() => {
    if (index < 0 || queue.length === 0) return
    let ni: number
    if (shuffle && queue.length > 1) {
      // Pick a random index different from current
      do { ni = Math.floor(Math.random() * queue.length) } while (ni === index)
    } else {
      ni = index + 1
      if (ni >= queue.length) {
        if (repeat === 'all') ni = 0
        else return  // pas de loop : on s'arrête
      }
    }
    setIndex(ni)
    setPlaying(true)
    apiRef.current?.load(queue[ni].videoId, true, queue[ni].audioUrl)
  }, [index, queue, shuffle, repeat])

  const prev = useCallback(() => {
    if (index < 0) return
    if (position > 3) {
      apiRef.current?.seekTo(0)
      return
    }
    const pi = Math.max(0, index - 1)
    setIndex(pi)
    setPlaying(true)
    apiRef.current?.load(queue[pi].videoId, true, queue[pi].audioUrl)
  }, [index, queue, position])

  const seek = useCallback((sec: number) => {
    apiRef.current?.seekTo(sec)
    setPosition(sec)
  }, [])

  const setVolume = useCallback((v: number) => {
    const clamped = Math.max(0, Math.min(100, Math.round(v)))
    volumeRef.current = clamped
    setVolumeState(clamped)
    apiRef.current?.setVolume?.(clamped)
    if (typeof window !== 'undefined') {
      try { localStorage.setItem('blc.volume', String(clamped)) } catch {}
    }
  }, [])

  const enqueue = useCallback((t: PlayerTrack | PlayerTrack[]) => {
    setQueue(q => [...q, ...(Array.isArray(t) ? t : [t])])
  }, [])

  // « Lire ensuite » : insère le morceau juste après le courant.
  const playNext = useCallback((t: PlayerTrack) => {
    setQueue(q => {
      if (index < 0) return [...q, t]
      const at = index + 1
      return [...q.slice(0, at), t, ...q.slice(at)]
    })
  }, [index])

  // Retire un morceau de la file (ajuste l'index courant si besoin).
  const removeFromQueue = useCallback((videoId: string) => {
    const remaining = queue.filter(x => x.videoId !== videoId)
    if (remaining.length === queue.length) return
    const removedCurrent = current?.videoId === videoId
    const before = queue.slice(0, Math.max(0, index)).filter(x => x.videoId !== videoId).length
    const nextIndex = index < 0 || !remaining.length ? -1 : Math.min(before, remaining.length - 1)
    setQueue(remaining)
    setIndex(nextIndex)
    if (removedCurrent) {
      setPosition(0)
      setDuration(0)
      if (nextIndex < 0) {
        apiRef.current?.playPause(false)
        setPlaying(false)
      } else {
        const track = remaining[nextIndex]
        apiRef.current?.load(track.videoId, playing, track.audioUrl)
      }
    }
  }, [queue, current, index, playing])

  // Réordonne la file : déplace l'élément `from` pour l'insérer AVANT l'index
  // `to` (0..length, comme un curseur entre les lignes). Le morceau EN COURS
  // reste suivi par IDENTITÉ (référence d'objet) → l'index le retrouve à sa
  // nouvelle position, la lecture ne saute pas. Insertion « avant `to` » : si
  // on descend un élément (from < to), sa cible réelle est décalée de 1 après
  // le retrait.
  const reorderQueue = useCallback((from: number, to: number) => {
    setQueue(prev => {
      if (from < 0 || from >= prev.length || to < 0 || to > prev.length) return prev
      const insertAt = from < to ? to - 1 : to
      if (insertAt === from) return prev
      const curTrack = index >= 0 ? prev[index] : null
      const arr = [...prev]
      const [moved] = arr.splice(from, 1)
      arr.splice(insertAt, 0, moved)
      if (curTrack) {
        const ni = arr.indexOf(curTrack)
        if (ni >= 0 && ni !== index) setIndex(ni)
      }
      return arr
    })
  }, [index])

  // REPRISE après reload forcé — appliquée sur le lecteur ATTACHÉ EN DERNIER.
  // ⚠️ Bug corrigé : au démarrage, PlayerSelector monte D'ABORD YoutubePlayer
  // (mode initial 'yt') PUIS bascule sur Html5AudioPlayer dès que l'APK est
  // détectée (window.AndroidAudio) → _attachPlayer est appelé DEUX FOIS. La
  // reprise s'exécutait sur le 1er appel (le lecteur YouTube TRANSITOIRE, qui
  // disparaît aussitôt) → le vrai lecteur (Html5Audio, celui qui reste) ne
  // recevait JAMAIS l'ordre de jouer → « tu n'as même pas fait démarrer la
  // musique ». Fix : on RE-REPORTE la reprise à chaque attache (debounce) pour
  // qu'elle s'exécute 400 ms après la DERNIÈRE — donc sur le lecteur qui reste.
  const resumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const applyResume = useCallback((api: PlayerApi | null) => {
    if (!api) return
    try {
      const raw = localStorage.getItem('blc.resume')
      if (!raw) return
      const data = JSON.parse(raw)
      // Une ancienne session garde son lecteur visible, en pause. Seul un
      // rechargement récent peut reprendre automatiquement la lecture.
      if (data && data.current && typeof data.current.videoId === 'string') {
        const q: PlayerTrack[] = Array.isArray(data.queue) && data.queue.length ? data.queue : [data.current]
        if (!q.every(t => t && typeof t.videoId === 'string' && typeof t.title === 'string')) return
        let idx = typeof data.index === 'number' ? data.index : q.findIndex(x => x.videoId === data.current.videoId)
        if (!Number.isInteger(idx) || idx < 0 || idx >= q.length) idx = 0
        const resumePlaying = !!data.playing && Date.now() - data.ts >= 0 && Date.now() - data.ts < 90_000
        setQueue(q)
        setIndex(idx)
        setPlaying(resumePlaying)
        const t = q[idx] || data.current
        api.load(t.videoId, resumePlaying, t.audioUrl)
        const pos = typeof data.position === 'number' ? data.position : 0
        if (pos > 1) setTimeout(() => {
          if (apiRef.current === api && stateRef.current.current?.videoId === t.videoId) {
            try { api.seekTo(pos) } catch {}
          }
        }, 900)
        resumeBeacon('applied vid=' + t.videoId + ' pos=' + pos + ' playing=' + !!data.playing)
      } else {
        resumeBeacon('SKIPPED (perimé ou incomplet) ageMs=' + (data?.ts ? Date.now() - data.ts : '?'))
      }
    } catch (e: any) { resumeBeacon('ERROR ' + (e?.message || '?')) }
  }, [])

  const _attachPlayer = useCallback((api: PlayerApi) => {
    apiRef.current = api
    // Réapplique le volume actuel quand un nouveau lecteur s'attache
    try { api.setVolume?.(volumeRef.current) } catch {}
    const active = stateRef.current
    if (active.current) {
      api.load(active.current.videoId, active.playing, active.current.audioUrl)
      return
    }
    if (!resumeAppliedRef.current) {
      if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current)
      resumeTimerRef.current = setTimeout(() => {
        if (resumeAppliedRef.current) return
        resumeAppliedRef.current = true
        applyResume(apiRef.current)   // apiRef = le DERNIER lecteur attaché
      }, 400)
    }
  }, [applyResume])

  const _onState = useCallback((s: Parameters<Ctx['_onState']>[0]) => {
    if (typeof s.ready === 'boolean')    setReady(s.ready)
    if (typeof s.playing === 'boolean')  setPlaying(s.playing)
    if (typeof s.duration === 'number')  setDuration(s.duration)
    if (typeof s.position === 'number')  setPosition(s.position)
  }, [])

  const _onEnded = useCallback(() => {
    // Mode "Répétition : un seul" → on relance la même piste
    if (repeat === 'one') {
      setIndex(i => {
        if (i < 0) return i
        const t = queue[i]
        if (t) {
          setPlaying(true)
          apiRef.current?.load(t.videoId, true, t.audioUrl)
        }
        return i
      })
      return
    }
    // Mode shuffle → piste aléatoire (≠ courante)
    if (shuffle && queue.length > 1) {
      setIndex(i => {
        let ni = i
        do { ni = Math.floor(Math.random() * queue.length) } while (ni === i)
        setPlaying(true)
        apiRef.current?.load(queue[ni].videoId, true, queue[ni].audioUrl)
        return ni
      })
      return
    }
    // Sinon : next, ou loop à 0 si "Répétition : tous"
    setIndex(i => {
      const ni = i + 1
      if (ni >= queue.length) {
        if (repeat === 'all' && queue.length > 0) {
          setPlaying(true)
          apiRef.current?.load(queue[0].videoId, true, queue[0].audioUrl)
          return 0
        }
        setPlaying(false)
        return i
      }
      setPlaying(true)
      apiRef.current?.load(queue[ni].videoId, true, queue[ni].audioUrl)
      return ni
    })
  }, [queue, repeat, shuffle])

  const value = useMemo<Ctx>(() => ({
    current, queue, playing, duration, ready, volume,
    videoFullscreen, audioOnly, videoPip, setVideoFullscreen, setAudioOnly, setVideoPip,
    repeat, shuffle, cycleRepeat, toggleShuffle,
    play, toggle, setPlay, stop, next, prev, seek, setVolume, enqueue, playNext, removeFromQueue, reorderQueue,
    _attachPlayer, _onState, _onEnded,
  }), [current, queue, playing, duration, ready, volume,
      videoFullscreen, audioOnly, videoPip, repeat, shuffle, cycleRepeat, toggleShuffle,
      play, toggle, setPlay, stop, next, prev, seek, setVolume, enqueue, playNext, removeFromQueue, reorderQueue,
      _attachPlayer, _onState, _onEnded])

  return (
    <PlayerContext.Provider value={value}>
      <PlayerPositionContext.Provider value={position}>
        {children}
      </PlayerPositionContext.Provider>
    </PlayerContext.Provider>
  )
}

export function usePlayer() {
  const ctx = useContext(PlayerContext)
  if (!ctx) throw new Error('usePlayer must be used inside <PlayerProvider>')
  return ctx
}

/** Position de lecture (secondes), mise à jour ~4×/s. Abonnez-vous à CE hook
 *  UNIQUEMENT dans les composants qui affichent la progression (barre, temps,
 *  paroles) — pas dans les listes — pour éviter les re-render inutiles. */
export function usePlayerPosition() {
  return useContext(PlayerPositionContext)
}

export function fmtTime(sec: number) {
  if (!isFinite(sec) || sec < 0) sec = 0
  const m = Math.floor(sec / 60)
  const s = Math.floor(sec % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}
